import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { API_BASE_URL } from '../../config';
import './FeedSidebarProfile.css';

interface AthleteSummary {
  user: {
    first_name: string;
    last_name: string;
    nationality: string | null;
    bike_type: string | null;
    strava_profile_picture: string | null;
    google_picture: string | null;
  };
  last7: {
    distance_m: number;
    elevation_m: number;
    moving_time_s: number;
    ride_count: number;
  };
  trend_pct: number;
  status: string;
  recent_activities: {
    id: number;
    name: string;
    distance: number;
    elevation: number;
    moving_time: number;
    start_date: string | null;
    activity_type: string;
  }[];
}

const FeedSidebarProfile: React.FC = () => {
  const { t } = useLanguage();
  const [data, setData] = useState<AthleteSummary | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSummary = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/web/athlete/summary`, {
          credentials: 'include',
        });
        if (res.ok) {
          setData(await res.json());
        }
      } catch { /* ignore */ }
      setLoading(false);
    };
    fetchSummary();
  }, []);

  const formatDistance = (meters: number) => {
    return (meters / 1000).toFixed(1) + ' km';
  };

  const formatElevation = (meters: number) => {
    return Math.round(meters) + ' m';
  };

  const formatTime = (seconds: number) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
  };

  const formatDate = (iso: string | null) => {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  };

  if (loading) {
    return (
      <div className="feed-sidebar-profile">
        <div className="sidebar-profile-skeleton">
          <div className="skeleton-circle" />
          <div className="skeleton-line medium" />
          <div className="skeleton-line short" />
        </div>
      </div>
    );
  }

  if (!data || !data.user) return null;

  const profile = data.user;
  const avatar = profile.strava_profile_picture || profile.google_picture;
  const initials = `${(profile.first_name || '')[0] || ''}${(profile.last_name || '')[0] || ''}`.toUpperCase();

  return (
    <div className="feed-sidebar-profile">
      <div className="sidebar-profile-header">
        {avatar ? (
          <img src={avatar} alt="" className="sidebar-profile-avatar" />
        ) : (
          <div className="sidebar-profile-avatar sidebar-profile-initials">{initials}</div>
        )}
        <div className="sidebar-profile-name">
          {profile.first_name} {profile.last_name}
        </div>
        {(profile.nationality || profile.bike_type) && (
          <div className="sidebar-profile-meta">
            {profile.nationality && <span>{profile.nationality}</span>}
            {profile.nationality && profile.bike_type && <span className="sidebar-meta-dot">·</span>}
            {profile.bike_type && <span>{profile.bike_type}</span>}
          </div>
        )}
      </div>

      <div className="sidebar-profile-section">
        <div className="sidebar-section-title">{t('feed.last7days')}</div>
        <div className="sidebar-stats-grid">
          <div className="sidebar-stat">
            <div className="sidebar-stat-value">{formatDistance(data.last7.distance_m)}</div>
            <div className="sidebar-stat-label">{t('feed.distance')}</div>
          </div>
          <div className="sidebar-stat">
            <div className="sidebar-stat-value">{formatElevation(data.last7.elevation_m)}</div>
            <div className="sidebar-stat-label">{t('feed.elevation')}</div>
          </div>
          <div className="sidebar-stat">
            <div className="sidebar-stat-value">{formatTime(data.last7.moving_time_s)}</div>
            <div className="sidebar-stat-label">{t('feed.time')}</div>
          </div>
        </div>
        {data.trend_pct !== 0 && (
          <div className={`sidebar-trend ${data.trend_pct > 0 ? 'trend-up' : 'trend-down'}`}>
            {data.trend_pct > 0
              ? t('feed.trendUp').replace('{pct}', Math.abs(data.trend_pct).toFixed(0))
              : t('feed.trendDown').replace('{pct}', Math.abs(data.trend_pct).toFixed(0))}
          </div>
        )}
      </div>

      {data.recent_activities.length > 0 && (
        <div className="sidebar-profile-section">
          <div className="sidebar-section-title">{t('feed.recentRides')}</div>
          <div className="sidebar-rides-list">
            {data.recent_activities.slice(0, 3).map(a => (
              <div key={a.id} className="sidebar-ride-item">
                <div className="sidebar-ride-name">{a.name}</div>
                <div className="sidebar-ride-meta">
                  {formatDate(a.start_date)} · {formatDistance(a.distance)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default FeedSidebarProfile;
